import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Statistic,
  Table,
  Select,
  Button,
  Spin,
  Typography,
  Tag,
  Space,
  Input,
} from "antd";

import {
  DollarCircleOutlined,
  ShoppingCartOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import axios from "axios";
import BASEURL from "../API/Config";
import * as XLSX from "xlsx";

const { Text } = Typography;
const { Option } = Select;

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];

const ComprehensiveReports = () => {
  const [loading, setLoading] = useState(true);
  const [salesPeriod, setSalesPeriod] = useState("monthly");
  const [salesSummary, setSalesSummary] = useState([]);
  const [revenueByCategory, setRevenueByCategory] = useState([]);
  const [revenueByPayment, setRevenueByPayment] = useState([]);
  const [orderStatus, setOrderStatus] = useState({});
  const [aov, setAov] = useState({});
  const [topProducts, setTopProducts] = useState([]);
  const [lowProducts, setLowProducts] = useState([]);

  const [topSearch, setTopSearch] = useState("");
  const [lowSearch, setLowSearch] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, [salesPeriod]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const [
        salesRes,
        catRes,
        payRes,
        statusRes,
        aovRes,
        topRes,
        lowRes,
      ] = await Promise.all([
        axios.get(`${BASEURL}/reports/sales-summary?period=${salesPeriod}`, config),
        axios.get(`${BASEURL}/reports/revenue-by-category`, config),
        axios.get(`${BASEURL}/reports/revenue-by-payment`, config),
        axios.get(`${BASEURL}/reports/order-status`, config),
        axios.get(`${BASEURL}/reports/aov`, config),
        axios.get(`${BASEURL}/reports/top-products`, config),
        axios.get(`${BASEURL}/reports/low-products`, config),
      ]);

      setSalesSummary(salesRes.data);
      setRevenueByCategory(catRes.data);
      setRevenueByPayment(payRes.data);
      setOrderStatus(statusRes.data);
      setAov(aovRes.data);
      setTopProducts(topRes.data);
      setLowProducts(lowRes.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (data, fileName, columns) => {
    let exportData = data;
    if (columns) {
      exportData = data.map((item, index) => {
        const row = { "S.No": index + 1 };
        columns.forEach((col) => {
          row[col.title] = col.dataIndex ? item[col.dataIndex] : col.render ? col.render(null, item) : "";
        });
        return row;
      });
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };


  const productColumns = [
    { title: "S.No", key: "index", width: 50, render: (_, __, i) => <span style={{ color: "#9ca3af" }}>{i + 1}</span> },
    { title: "Product Title", dataIndex: "title", key: "title", render: (t) => <span style={{ fontWeight: 600 }}>{t}</span> },
    { title: "Total Sold", dataIndex: "totalSold", key: "totalSold", align: "center" },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (val) => <span style={{ fontWeight: 700 }}>₹{val.toLocaleString()}</span>,
    },
  ];

  const downloadAllReports = () => {

    const wb = XLSX.utils.book_new();
    
    const sections = [
      { name: "Sales Summary", data: salesSummary, cols: [{ title: "Period", dataIndex: "_id" }, { title: "Revenue", dataIndex: "totalSales" }, { title: "Orders", dataIndex: "orderCount" }] },
      { name: "Category Revenue", data: revenueByCategory, cols: [{ title: "Category", dataIndex: "_id" }, { title: "Revenue", dataIndex: "revenue" }, { title: "Sold", dataIndex: "count" }] },
      { name: "Payment Revenue", data: revenueByPayment, cols: [{ title: "Method", dataIndex: "_id" }, { title: "Revenue", dataIndex: "revenue" }, { title: "Orders", dataIndex: "count" }] },
      { name: "Top Products", data: topProducts, cols: productColumns },
      { name: "Low Performing Products", data: lowProducts, cols: productColumns }
    ];

    sections.forEach(section => {
      const exportData = section.data.map((item, index) => {
        const row = { "S.No": index + 1 };
        section.cols.forEach((col) => {
          row[col.title] = col.dataIndex ? item[col.dataIndex] : col.render ? col.render(null, item) : "";
        });
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, section.name);
    });

    XLSX.writeFile(wb, "Comprehensive_Analytics_Report.xlsx");
  };


  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" tip="Loading Reports..." />
      </div>
    );
  }

  const orderStatusData = Object.keys(orderStatus).map((key) => ({
    name: key,
    value: orderStatus[key],
  }));

  const filteredTop = topProducts.filter(p => String(p.title || "").toLowerCase().includes(topSearch.toLowerCase()));
  const filteredLow = lowProducts.filter(p => String(p.title || "").toLowerCase().includes(lowSearch.toLowerCase()));

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1 }}>Reports</div>
          <h2 style={{ margin: 0, fontFamily: "Outfit,sans-serif", fontSize: 22, fontWeight: 700, color: "#212121" }}>Analytics Dashboard</h2>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData} style={{ borderRadius: 4 }}>Refresh</Button>
          <Button 
            type="primary" 
            icon={<FileExcelOutlined />} 
            onClick={downloadAllReports}
            style={{ background: "#107c41", borderColor: "#107c41", borderRadius: 4, fontWeight: 600 }}
          >
            Download All Sections
          </Button>
        </Space>
      </div>

      {/* Top Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Total Revenue</span>}
              value={aov.totalRevenue}
              precision={2}
              prefix="₹"
              valueStyle={{ color: "#3f8600", fontWeight: 700 }}
              icon={<DollarCircleOutlined style={{ color: "#3f8600" }} />}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Total Orders</span>}
              value={aov.totalOrders}
              prefix={<ShoppingCartOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#212121", fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Average Order Value</span>}
              value={aov.averageValue}
              precision={2}
              prefix="₹"
              valueStyle={{ color: "#722ed1", fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Text style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Order Completion Rate</Text>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#faad14", marginTop: 4 }}>
              {((orderStatus.Completed / aov.totalOrders) * 100).toFixed(1)}%
            </div>
          </div>
        </Col>
      </Row>

      {/* Sales Summary Chart */}
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Sales Summary</span>
          <Space>
            <Select value={salesPeriod} onChange={setSalesPeriod} style={{ width: 120 }} size="small">
              <Option value="daily">Daily</Option>
              <Option value="weekly">Weekly</Option>
              <Option value="monthly">Monthly</Option>
              <Option value="yearly">Yearly</Option>
            </Select>
            <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(salesSummary, "Sales_Summary", [
              { title: "Period", dataIndex: "_id" },
              { title: "Revenue (₹)", dataIndex: "totalSales" },
              { title: "Total Orders", dataIndex: "orderCount" }
            ])} style={{ borderRadius: 4 }}>Export</Button>
          </Space>
        </div>
        <div style={{ padding: 20, height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={salesSummary}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="_id" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
              <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
              <Legend />
              <Bar dataKey="totalSales" name="Total Sales" fill="#1890ff" radius={[4, 4, 0, 0]} barSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Revenue by Category */}
        <Col xs={24} lg={12}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", height: "100%" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Revenue by Category</span>
              <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(revenueByCategory, "Revenue_By_Category", [
                { title: "Category", dataIndex: "_id" },
                { title: "Revenue (₹)", dataIndex: "revenue" },
                { title: "Quantity Sold", dataIndex: "count" }
              ])} style={{ borderRadius: 4 }}>Export</Button>
            </div>
            <div style={{ padding: 20, height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueByCategory} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <YAxis dataKey="_id" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} width={100} />
                  <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} cursor={{ fill: "rgba(0, 136, 254, 0.05)" }} />
                  <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} barSize={20}>
                    {revenueByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>

        {/* Order Status Distribution */}
        <Col xs={24} lg={12}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", height: "100%" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Order Status Distribution</span>
              <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(orderStatusData, "Order_Status_Report", [
                { title: "Status", dataIndex: "name" },
                { title: "Count", dataIndex: "value" }
              ])} style={{ borderRadius: 4 }}>Export</Button>
            </div>
            <div style={{ padding: 20, height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={orderStatusData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} width={100} />
                  <Tooltip cursor={{ fill: "rgba(0, 136, 254, 0.05)" }} />
                  <Bar dataKey="value" name="Orders" radius={[0, 4, 4, 0]} barSize={20}>
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        {/* Top Selling Products */}
        <Col xs={24} lg={24}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "nowrap" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15, whiteSpace: "nowrap" }}>Top Selling Products</span>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "flex-end" }}>
                <Input 
                  prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} 
                  placeholder="Search..."
                  size="small"
                  value={topSearch}
                  onChange={(e) => setTopSearch(e.target.value)}
                  style={{ width: 140, borderRadius: 4 }} 
                />
                <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(topProducts, "Top_Products", productColumns)} style={{ borderRadius: 4 }}>Export</Button>
              </div>
            </div>
            <Table
              dataSource={filteredTop}
              columns={productColumns}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
              size="middle"
              rowKey="_id"
            />
          </div>
        </Col>

        {/* Low Performing Products */}
        <Col xs={24} lg={24}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "nowrap" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15, whiteSpace: "nowrap" }}>Low Performing Products</span>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "flex-end" }}>
                <Input 
                  prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} 
                  placeholder="Search..."
                  size="small"
                  value={lowSearch}
                  onChange={(e) => setLowSearch(e.target.value)}
                  style={{ width: 140, borderRadius: 4 }} 
                />
                <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(lowProducts, "Low_Performing_Products", productColumns)} style={{ borderRadius: 4 }}>Export</Button>
              </div>
            </div>
            <Table
              dataSource={filteredLow}
              columns={productColumns}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
              size="middle"
              rowKey="_id"
            />
          </div>
        </Col>
      </Row>

      {/* Revenue by Payment Method */}
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", marginTop: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Revenue by Payment Method</span>
          <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(revenueByPayment, "Revenue_By_Payment", [
            { title: "Method", dataIndex: "_id" },
            { title: "Revenue (₹)", dataIndex: "revenue" },
            { title: "Order Count", dataIndex: "count" }
          ])} style={{ borderRadius: 4 }}>Export</Button>
        </div>
        <div style={{ padding: 20 }}>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={revenueByPayment}
                      dataKey="revenue"
                      nameKey="_id"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {revenueByPayment.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col xs={24} md={16}>
              <Table
                dataSource={revenueByPayment}
                columns={[
                  { title: "S.No", key: "index", width: 50, render: (_, __, i) => <span style={{ color: "#9ca3af" }}>{i + 1}</span> },
                  { title: "Payment Method", dataIndex: "_id", key: "method", render: (m) => <span style={{ fontWeight: 600 }}>{m}</span> },
                  { title: "Orders", dataIndex: "count", key: "count", align: "center" },
                  {
                    title: "Total Revenue",
                    dataIndex: "revenue",
                    key: "revenue",
                    render: (val) => <span style={{ fontWeight: 700 }}>₹{val.toLocaleString()}</span>,
                  },
                ]}
                pagination={false}
                size="middle"
                rowKey="_id"
              />
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveReports;


